import { useEffect, useRef, useState, useContext } from 'react';

import Icon from 'components/helpers/Icon';
import styles from './Header.module.scss';
import classNames from 'classnames/bind';
import SearchBox from './SearchBox';

import { contextType, HeaderContext } from './Header';
import { SearchIconProps } from './HeaderTypes';
import { useDictionary } from 'lib/utils';

const SearchIcon = (props: SearchIconProps): React.JSX.Element | null => {
  const cx = classNames.bind(styles);
  const $ariaLabelSearch = useDictionary('Aria-Search');

  const { currentLang, datasourceLanguage } = useContext(HeaderContext) as contextType;
  const { pageLanguages, isMobile, searchPageUrl, searchLinkParam } = props;

  const $highlighted = pageLanguages && pageLanguages.length <= 1;

  const [showMenu, setShowMenu] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const onCloseCallback = () => setShowMenu(false);

  useEffect(() => {
    const hideMenu = () => {
      if (showMenu) {
        setShowMenu(false);
      }
    };

    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hideMenu();
      }
    };

    const handleOutsideClick = (event: Event) => {
      if (!ref.current?.contains(event.target as Element) && !isMobile) {
        hideMenu();
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    window.addEventListener('mousedown', handleOutsideClick);

    return () => {
      window.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [ref, showMenu, isMobile]);

  return (
    <>
      <div className={cx('search-icon', 'flyoutContainer')} ref={ref} tabIndex={-1}>
        <button
          type="button"
          aria-controls="search-box"
          aria-expanded={showMenu}
          aria-label={$ariaLabelSearch}
          title={$ariaLabelSearch}
          className={cx({ 'search-icon-button': true, selected: showMenu, highlighted: $highlighted })}
          onClick={() => setShowMenu(!showMenu)}
          lang={datasourceLanguage ?? currentLang}
        >
          <Icon variant="search16" className={cx('search-header')} />
        </button>

        {!isMobile && showMenu ? (
          <SearchBox
            isMobile={isMobile}
            displayMenu={showMenu}
            searchPageUrl={searchPageUrl}
            searchLinkParam={searchLinkParam}
          />
        ) : null}
      </div>

      {/* Mobile display is fixed at bottom of the screen - its easier to manage outside parent div*/}
      {isMobile && showMenu ? (
        <SearchBox
          isMobile={isMobile}
          displayMenu={showMenu}
          onClose={onCloseCallback}
          displayType="mobile"
          searchPageUrl={searchPageUrl}
          searchLinkParam={searchLinkParam}
        />
      ) : null}
    </>
  );
};

export default SearchIcon;
